/* Nova — Web Push helper
 *
 * Small wrapper around the browser PushManager + our /api/push endpoints.
 * Exposes:
 *   getPushSupport()                — feature-detection summary
 *   getCurrentSubscription()        — current PushSubscription (or null)
 *   subscribeToPush()               — registers SW, asks permission, subscribes
 *   unsubscribeFromPush()           — unsubscribes and tells the backend
 *   sendTestPush()                  — fires a one-shot test notification
 */
import api from "@/lib/api";

function urlBase64ToUint8Array(base64String) {
  const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, "+").replace(/_/g, "/");
  const raw = window.atob(base64);
  const out = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; ++i) out[i] = raw.charCodeAt(i);
  return out;
}

export function getPushSupport() {
  // Service Worker + PushManager + Notification all need to exist. iOS
  // Safari only exposes them in standalone PWA mode.
  const sw = "serviceWorker" in navigator;
  const push = "PushManager" in window;
  const noti = "Notification" in window;
  const standalone =
    window.matchMedia?.("(display-mode: standalone)").matches ||
    // legacy iOS check
    window.navigator?.standalone === true;
  const iOS = /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
  return {
    supported: sw && push && noti,
    sw, push, noti,
    iosRequiresPwa: iOS && !standalone,
    permission: noti ? Notification.permission : "denied",
  };
}

async function ensureSwRegistered() {
  if (!("serviceWorker" in navigator)) throw new Error("Service workers not supported");
  // Use an absolute-path scope to match where sw.js is hosted.
  const reg =
    (await navigator.serviceWorker.getRegistration("/")) ||
    (await navigator.serviceWorker.register("/sw.js", { scope: "/" }));
  // Wait for the SW to become active.
  if (reg.installing || reg.waiting) await navigator.serviceWorker.ready;
  return reg;
}

export async function getCurrentSubscription() {
  if (!("serviceWorker" in navigator)) return null;
  const reg = await navigator.serviceWorker.getRegistration("/");
  if (!reg) return null;
  return reg.pushManager.getSubscription();
}

export async function subscribeToPush() {
  const support = getPushSupport();
  if (!support.supported) {
    throw new Error(
      support.iosRequiresPwa
        ? "On iPhone, add Nova to your Home Screen first, then open it from there."
        : "Push notifications aren't supported by this browser.",
    );
  }
  // Ask permission ONLY in response to a user gesture (caller guarantees this).
  const perm = await Notification.requestPermission();
  if (perm !== "granted") throw new Error("Permission denied");

  const reg = await ensureSwRegistered();

  // Fetch the server's public VAPID key.
  const { data } = await api.get("/push/vapid-key");
  const publicKey = data?.public_key;
  if (!publicKey) throw new Error("Server is missing push configuration");

  // Re-use existing subscription if any.
  let sub = await reg.pushManager.getSubscription();
  if (!sub) {
    sub = await reg.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: urlBase64ToUint8Array(publicKey),
    });
  }

  // Send the subscription to the backend so the scheduler can target it.
  const subJson = sub.toJSON();
  await api.post("/push/subscribe", {
    endpoint: subJson.endpoint,
    keys: subJson.keys,
  });

  return sub;
}

export async function unsubscribeFromPush() {
  const sub = await getCurrentSubscription();
  if (!sub) return;
  try {
    await api.post("/push/unsubscribe", { endpoint: sub.endpoint });
  } catch (e) {
    /* still try to unsubscribe locally */
  }
  await sub.unsubscribe();
}

export async function sendTestPush() {
  const { data } = await api.post("/push/test", {});
  return data;
}
